import { useEffect, useMemo, useState } from 'react'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { RoleType, Space, SpaceType } from '@tribeplatform/gql-client/types'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { SocialShareButtons } from '@tribeplatform/react-components/ShareModal'
import {
  useAuthMember,
  useSpaces,
  useInvitationLink,
  useInviteMembers,
  useNetwork,
} from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { Divider } from '@tribeplatform/react-ui-kit/Divider'
import { SpinnerIcon } from '@tribeplatform/react-ui-kit/icons'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useAdminOnboarding } from '../../hooks/useAdminOnboarding'
import { pickIfExists } from '../../lib/arrays.utils'
import { EmailVerificationBanner } from './EmailVerificationBanner'
import { ShareInviteMemberLink } from './ShareInviteMemberLink'
import { UserImportContext, UserImportContextProps } from './UserImportContext'
import { UserImportForm } from './UserImportForm'
import { UserImportFormProps } from './useUserImportForm'

export interface UserImportModalProps {
  open: boolean
  onClose: () => void
}

export const UserImportModal = ({ open, onClose }) => {
  const { $t } = useI18n()
  const { data: network, isLoading: isNetworkLoading } = useNetwork({
    fields: {
      defaultSpaces: { image: 'all' },
      roles: 'all',
      authMemberProps: 'all',
    },
  })

  const { isAdmin } = useAuthMember()

  const { data, isLoading: isSpacesLoading } = useSpaces({
    fields: { image: 'basic' },
    variables: { limit: 30, type: [SpaceType.Group] },
  })
  const { completeStep } = useAdminOnboarding()
  const spaces = useMemo(
    () => simplifyPaginatedResult<Space>(data)?.nodes || [],
    [data],
  )

  const isLoading = isNetworkLoading || isSpacesLoading

  const { data: memberInvitationLink } = useInvitationLink()
  const { mutateAsync: inviteMembers, isLoading: isInvitingMembers } =
    useInviteMembers()

  const [defaultSpaces, setDefaultSpaces] = useState<Space[]>([])

  useEffect(() => {
    const firstSpace = spaces?.[0]
    if (network?.defaultSpaces && network.defaultSpaces.length > 0) {
      setDefaultSpaces(pickIfExists(network?.defaultSpaces, spaces, 'id'))
    } else if (firstSpace) {
      setDefaultSpaces([firstSpace])
    } else {
      setDefaultSpaces([])
    }
  }, [network?.defaultSpaces, spaces])

  const [canInviteMembers] = hasScopesPermission(network, ['inviteMembers'])
  const context: UserImportContextProps = {
    defaultSpaces,
    spaces,
    loading: isLoading,
    hasInviteDefaultsPermission: canInviteMembers,
    defaultRole:
      network?.roles?.find(it => it.type === RoleType.member) ||
      network?.roles?.[0],
    roles: network?.roles.filter(r => r.type !== RoleType.guest),
  }

  const onCompleted = async (formValues: UserImportFormProps) => {
    await inviteMembers(
      {
        input: {
          invitees: formValues.entries.map(it => ({
            email: it.email?.trim(),
            name: it.name?.trim(),
          })),
          defaultSpacesIds: formValues.spaces.map(it => it.id),
          invitationMessage: formValues.customMessage || undefined,
          roleId: formValues.role?.id,
        },
      },
      {
        onSuccess: () => {
          completeStep({ step: 'invite-members' })
          toast({
            title: $t({
              defaultMessage:
                'Your invitations are in the send queue and will go out shortly',
              id: 'UserImport.InvitesSent',
            }),
            status: 'success',
          })
          onClose()
        },
        onError: error => {
          const message = error?.response?.errors
            ?.map(e => e.message)
            ?.join('\n')
          toast({
            title: $t({
              defaultMessage: 'Invite failed',
              id: 'UserImport.InviteesFailed',
            }),
            description:
              message ||
              $t({
                defaultMessage:
                  'Something went wrong and we were unable to send out the invitations. Please try again.',
                id: 'UserImport.InviteesFailed.Description',
              }),
            status: 'error',
          })
        },
      },
    )
  }

  return (
    <UserImportContext.Provider value={context}>
      <Modal open={open} onClose={onClose} size="2xl">
        <Modal.Header
          title={
            <T
              defaultMessage="Invite {MEMBERS} to {community_name}"
              id="UserImport.Header"
              values={{
                community_name: network?.name,
              }}
            />
          }
        />
        <Modal.Content>
          {isLoading ? (
            <div className="flex items-center justify-center h-32	">
              <SpinnerIcon className="animate-spin" />
            </div>
          ) : (
            <>
              <ShareInviteMemberLink url={memberInvitationLink?.link} />
              {context.hasInviteDefaultsPermission && isAdmin ? (
                <>
                  <Divider>
                    <T
                      defaultMessage="Or invite manually"
                      id="UserImport.Header.Manual"
                    />
                  </Divider>

                  <EmailVerificationBanner />

                  <UserImportForm
                    onSubmit={onCompleted}
                    onCancel={onClose}
                    loading={isInvitingMembers}
                  />
                </>
              ) : (
                <div className="mt-5">
                  <SocialShareButtons
                    url={memberInvitationLink?.link}
                    title={
                      <T
                        defaultMessage="Join me on {community_name}!"
                        id="UserImport.SocialInviteTitle"
                        values={{
                          community_name: network.name,
                        }}
                      />
                    }
                  />
                </div>
              )}
            </>
          )}
        </Modal.Content>
      </Modal>
    </UserImportContext.Provider>
  )
}
